<template>
	<WaitDialog :isActive="loading" title="Loading RSM data..." />

	<v-container fluid>
			<!-- Summary grid -->
			<v-row>
				<v-col cols="12">
					<v-card class="text-left">
						<v-data-table
							:headers="headers"
							:items="summaryRows"
							:loading="loading"
							density="comfortable"
							class="elevation-0"
							no-data-text="No data returned"
							items-per-page="-1"
							hide-default-footer
							hover
						>
							<template #top>
								<v-toolbar flat>
									<v-toolbar-title>RSM Management</v-toolbar-title>
									<v-divider class="mx-4" inset vertical />
								<m-btn color="primary" prepend-icon="mdi-refresh" @click="loadSummary(selectedEnvironment)">
										Refresh
									</m-btn>
								</v-toolbar>
							</template>

							<!-- Trash-can action column -->
							<!-- eslint-disable vue/valid-v-slot -->
							<template v-slot:item.actions="{ item }">
								<v-btn
									icon
									size="small"
									variant="text"
									color="error"
									:loading="deletingRegCode === item.salesField"
									:disabled="loading || deletingRegCode !== null"
									:title="`Delete ${item.salesField}`"
									@click="deleteRow(item.salesField)"
								>
									<v-icon>mdi-trash-can-outline</v-icon>
								</v-btn>
							</template>
						</v-data-table>
					</v-card>
				</v-col>
			</v-row>

			<!-- Input row -->
			<v-row class="mt-4">
				<v-col cols="12">
					<v-card class="text-left">
						<v-card-text>
							<v-row>
								<v-col cols="12" md="4">
									<v-text-field
										ref="firstFieldRef"
										v-model="inputMktDesc"
										label="DMM / RSM"
										placeholder="Person name"
										density="comfortable"
										variant="outlined"
										hide-details
										:disabled="loading"
									@keydown.enter.prevent="addRow"
									/>
								</v-col>
								<v-col cols="12" md="2">
									<v-text-field
										v-model="inputRegCode"
										label="Sales Field"
										placeholder="e.g. 01"
										density="comfortable"
										variant="outlined"
										hide-details
										:disabled="loading"
									@keydown.enter.prevent="addRow"
									/>
								</v-col>
								<v-col cols="12" md="6">
									<v-text-field
										v-model="inputRegDesc"
										label="Sales Field Name"
										placeholder="Region description"
										density="comfortable"
										variant="outlined"
										hide-details
										:disabled="loading"
										@keydown.enter.prevent="addRow"
									/>
								</v-col>
							</v-row>
						</v-card-text>
						<v-divider />
						<v-card-text class="text-caption text-medium-emphasis pt-2 pb-3">
							<v-icon size="x-small" class="mr-1">mdi-information-outline</v-icon>
							<strong>Sales Field</strong> is required — it links the DMM and Sales Field Name together.
							The DMM / RSM with the Sales Field, assigns a name, or changes the existing name.
							Provide all three fields to create a new entry, or change all of an existing entry.
						</v-card-text>
					</v-card>
				</v-col>
			</v-row>
	</v-container>
</template>

<script lang="ts">
import { ref, onMounted, nextTick, inject } from 'vue';
import { useRsmApi } from '@/composables/UseRsmApi';
import type { RsmSummaryRow } from '@/models/RsmViewModels';
import MBtn from '@/components/BaseButton.vue';
import WaitDialog from '@/components/WaitDialog.vue';
import { Tell } from '@/services/Tell';

export default {
	name: 'RsmUpdates',
	components: {
		MBtn,
		WaitDialog,
	},
	route: {
		path: '/SSAM/rsm-updates',
		meta: {
			requiresAuth: true,
			title: 'RSM Management',
		},
	},

	setup() {
		const api = useRsmApi();
		const tell = inject('tell') as Tell;

		// ── State ──────────────────────────────────────────────────────────
		const summaryRows = ref<RsmSummaryRow[]>([]);
		const loading = ref(false);
		const deletingRegCode = ref<string | null>(null);

		const selectedEnvironment = ref('snd4');

		const inputMktDesc = ref('');
		const inputRegCode = ref('');
		const inputRegDesc = ref('');

		const firstFieldRef = ref<InstanceType<typeof import('vuetify/components').VTextField> | null>(null);

		// ── Table headers ─────────────────────────────────────────────────
		const headers = [
			{ title: 'DMM / RSM',        key: 'dmmRsm',        sortable: true },
			{ title: 'Sales Field',      key: 'salesField',     sortable: true,  width: 130 },
			{ title: 'Sales Field Name', key: 'salesFieldName', sortable: true },
			{ title: '',                 key: 'actions',        sortable: false, width: 56, align: 'center' as const },
		];

		// ── Helpers ───────────────────────────────────────────────────────
		function clearInputs() {
			inputMktDesc.value = '';
			inputRegCode.value = '';
			inputRegDesc.value = '';
		}
		async function focusFirstField() {
			await nextTick();
			firstFieldRef.value?.focus();
		}

		// ── Data loading ──────────────────────────────────────────────────
		async function loadSummary(environment: string) {
			loading.value = true;
			try {
				summaryRows.value = await api.getSummary(environment);
			} catch (e: unknown) {
				tell.warning(`Error loading data: ${(e as Error).message}`);
				summaryRows.value = [];
			} finally {
				loading.value = false;
			}
		}

		// ── Add row (Enter) ───────────────────────────────────────────────
		async function addRow() {
			if (!inputRegCode.value.trim()) return;

			loading.value = true;
			try {
				const code = inputRegCode.value.trim();
				const messages: string[] = [];

				if (inputRegDesc.value.trim()) {
					const r = await api.upsertRegion({ environment: selectedEnvironment.value, regCode: code, regDesc: inputRegDesc.value.trim() });
					if (r.rowsAffected > 0)
						messages.push(r.wasInserted ? `Region '${code}' added.` : `Region '${code}' updated.`);
				}

				if (inputMktDesc.value.trim()) {
					const r = await api.upsertAssignment({ environment: selectedEnvironment.value, regCode: code, mktDesc: inputMktDesc.value.trim() });
					if (r.rowsAffected > 0)
						messages.push(r.wasInserted ? `Assignment '${code}' added.` : `Assignment '${code}' updated.`);
				}

				if (messages.length) tell.success(messages.join('  |  '));
				clearInputs();
				await loadSummary(selectedEnvironment.value);
			} catch (e: unknown) {
				tell.warning(`Error applying updates: ${(e as Error).message}`);
			} finally {
				loading.value = false;
				await focusFirstField();
			}
		}

		// ── Delete row (trash icon) ───────────────────────────────────────
		async function deleteRow(regCode: string) {
			loading.value = true;
			deletingRegCode.value = regCode;
			try {
				const [reg, asgn] = await Promise.all([
					api.deleteRegion(regCode),
					api.deleteAssignment(regCode),
				]);
				const total = reg.rowsDeleted + asgn.rowsDeleted;
				if (total === 0)
					tell.warning(`Sales Field '${regCode}' was not found — nothing deleted.`);
				else
					tell.success(`Sales Field '${regCode}' deleted: ${reg.rowsDeleted} region row(s), ${asgn.rowsDeleted} assignment row(s) removed.`);
				await loadSummary(selectedEnvironment.value);
			} catch (e: unknown) {
				tell.error(`Error deleting '${regCode}': ${(e as Error).message}`);
			} finally {
				deletingRegCode.value = null;
				loading.value = false;
			}
		}

		// ── Lifecycle ─────────────────────────────────────────────────────
		onMounted(() => loadSummary(selectedEnvironment.value));

		return {
			headers,
			summaryRows,
			loading,
			selectedEnvironment,
			deletingRegCode,
			inputMktDesc,
			inputRegCode,
			inputRegDesc,
			firstFieldRef,
			addRow,
			deleteRow,
			loadSummary,
		};
	},
};
</script>


